/**
 * CreativePreviews — compact per-concept platform previews.
 *
 * Pure function of (campaignState, concept): Meta feed card, Meta story
 * frame, Google search text ad, and Google display unit, all rendered
 * client-side from live state — no LLM involvement. Because they read the
 * same state the canvas edits, any chat patch or inline edit updates them
 * instantly.
 */

import React, { useState } from 'react';
import { ConceptArt } from './conceptArt';
import { AdPreviewModal, type PreviewFormat } from './AdPreviewModal';
import { PlatformLogo, type PlatformKey } from '@/components/icons/PlatformLogos';
import type { CampaignState, CreativeConcept } from './types';
import { getStrategyPlatforms } from './types';

const isEnabled = (concept: CreativeConcept, format: string) =>
  concept.renditions?.some((r) => r.format === format && r.enabled !== false) ?? false;

const hasGoogleRendition = (concept: CreativeConcept) =>
  concept.renditions?.some(
    (r) => (r.platform === 'google' || r.platform === 'google_ads') && r.enabled !== false,
  ) ?? false;

const hasPinterestRendition = (concept: CreativeConcept) =>
  concept.renditions?.some(
    (r) => r.platform === 'pinterest' && r.enabled !== false,
  ) ?? false;

const hasTwitterRendition = (concept: CreativeConcept) =>
  concept.renditions?.some(
    (r) => (r.platform === 'twitter' || r.platform === 'x') && r.enabled !== false,
  ) ?? false;

const Label: React.FC<{ children: React.ReactNode; platform: PlatformKey }> = ({ children, platform }) => (
  <div className="mb-1.5 flex items-center gap-1.5 text-label uppercase text-ink-3">
    <PlatformLogo platform={platform} size="2xs" />
    {children}
  </div>
);

export interface CreativePreviewsProps {
  state: CampaignState;
  concept: CreativeConcept;
  index: number;
  uploadedImages?: Array<{ url: string }>;
  onAdjustmentSave?: (conceptId: string, format: PreviewFormat, crop: import('@/components/media/mediaAdjustmentTypes').CropParams, targetW: number, targetH: number) => void;
}

export const CreativePreviews: React.FC<CreativePreviewsProps> = ({ state, concept, index, uploadedImages, onAdjustmentSave }) => {
  const [previewFormat, setPreviewFormat] = useState<PreviewFormat | null>(null);

  // Use uploaded images as fallback when the concept has no generated asset yet
  const fallbackUrl = uploadedImages?.[index % (uploadedImages?.length || 1)]?.url;
  const sharedMeta = state.messaging?.meta ?? {};
  // Gemini emits canonical `google_ads`; older responses use `google`. Read whichever holds copy.
  const sharedGoogle = (state.messaging as any)?.google_ads ?? state.messaging?.google ?? {};
  const conceptCopy = concept.copy;
  const headline =
    conceptCopy?.meta?.headline ?? sharedMeta.headline ?? state.name ?? 'Your headline';
  const body = conceptCopy?.meta?.body ?? sharedMeta.body ?? '';
  const gHeadline =
    conceptCopy?.google?.headlines?.[0] ?? sharedGoogle.headlines?.[0] ?? headline;
  const gDescription =
    conceptCopy?.google?.descriptions?.[0] ?? sharedGoogle.descriptions?.[0] ?? body;

  // Only show platform mockups for platforms the strategy actually includes.
  const strategyPlatforms = getStrategyPlatforms(state);
  const inStrategy = (p: string) =>
    strategyPlatforms.size === 0 || strategyPlatforms.has(p);

  const showFeed = isEnabled(concept, 'feed_1x1') && inStrategy('meta');
  const showStory = isEnabled(concept, 'story_9x16') && inStrategy('meta');
  const showPinterest = hasPinterestRendition(concept) && inStrategy('pinterest');
  const showTwitter = hasTwitterRendition(concept) && inStrategy('twitter');
  const tweetText =
    conceptCopy?.twitter?.tweet ??
    state.messaging?.twitter?.tweet ??
    body ??
    headline;
  const showDisplay = hasGoogleRendition(concept) && (inStrategy('google') || inStrategy('google_ads'));
  const showSearch = (sharedGoogle.headlines?.length ?? 0) > 0 && (inStrategy('google') || inStrategy('google_ads'));

  const previewHint = 'cursor-pointer ring-transparent transition-all hover:ring-2 hover:ring-primary/50 hover:ring-offset-1 rounded-lg';

  // Saved per-format crop → CSS object-position focal point, so the compact preview
  // centres on the region set in the full-size modal (and applied at publish) instead
  // of a plain centre crop that can cut the subject.
  const cropPos = (fmt: string): string | undefined => {
    const c = concept.adjustments?.[fmt]?.crop as
      | { x_frac: number; y_frac: number; w_frac: number; h_frac: number }
      | undefined;
    if (!c) return undefined;
    return `${(c.x_frac + c.w_frac / 2) * 100}% ${(c.y_frac + c.h_frac / 2) * 100}%`;
  };

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-3.5 border-t border-border pt-3">
        {/* Meta carousel — shown when this concept is a multi-card carousel. */}
        {(concept.cards?.length ?? 0) >= 2 && inStrategy('meta') && (
          <div className="w-[210px]">
            <Label platform="meta">Meta carousel</Label>
            <div
              className={`overflow-hidden rounded-lg border border-border bg-white text-[10.5px] text-gray-900 ${previewHint}`}
              onClick={() => setPreviewFormat('carousel')}
              title="Click to preview full size"
            >
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <span className="h-[18px] w-[18px] flex-shrink-0 rounded-full bg-[#4267B2]" />
                <span className="leading-tight">
                  <b className="block text-[10.5px]">Your Brand</b>
                  <span className="block text-[9px] text-gray-500">Sponsored</span>
                </span>
              </div>
              <div className="flex gap-1 overflow-x-auto p-1">
                {concept.cards!.map((card, i) => (
                  <div key={card.id ?? i} className="w-[92px] flex-shrink-0">
                    <div className="relative aspect-square overflow-hidden rounded bg-gray-100">
                      {card.assetUrl && (
                        <img src={card.assetUrl} alt="" className="h-full w-full object-cover" />
                      )}
                    </div>
                    {card.headline && (
                      <div className="truncate px-0.5 pt-0.5 text-[9px] font-semibold">{card.headline}</div>
                    )}
                  </div>
                ))}
              </div>
              {body && <div className="px-2 py-1.5 leading-snug">{body}</div>}
            </div>
          </div>
        )}

        {/* Meta feed card */}
        {showFeed && (
          <div className="w-[185px]">
            <Label platform="meta">Meta feed</Label>
            <div
              className={`overflow-hidden rounded-lg border border-border bg-white text-[10.5px] text-gray-900 ${previewHint}`}
              onClick={() => setPreviewFormat('feed_1x1')}
              title="Click to preview full size"
            >
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <span className="h-[18px] w-[18px] flex-shrink-0 rounded-full bg-[#4267B2]" />
                <span className="leading-tight">
                  <b className="block text-[10.5px]">Your Brand</b>
                  <span className="block text-[9px] text-gray-500">Sponsored</span>
                </span>
              </div>
              <div className="relative aspect-[4/5] overflow-hidden">
                <ConceptArt concept={concept} index={index} fallbackUrl={fallbackUrl} objectPosition={cropPos('feed_1x1')} format="feed_1x1" />
              </div>
              {body && <div className="px-2 py-1.5 leading-snug">{body}</div>}
              <div className="flex items-center gap-1.5 bg-[#f0f2f5] px-2 py-1.5 font-semibold">
                <span className="flex-1 truncate text-[10px]">{headline}</span>
                <span className="rounded bg-[#e4e6eb] px-2 py-1 text-[9.5px] font-semibold">Shop now</span>
              </div>
            </div>
          </div>
        )}

        {/* Meta story frame */}
        {showStory && (
          <div className="w-[105px]">
            <Label platform="meta">Meta story</Label>
            <div
              className={`relative aspect-[9/16] overflow-hidden border border-border ${previewHint}`}
              onClick={() => setPreviewFormat('story_9x16')}
              title="Click to preview full size"
            >
              <ConceptArt concept={concept} index={index} fallbackUrl={fallbackUrl} objectPosition={cropPos('story_9x16')} format="story_9x16" />
              <div className="absolute bottom-2 left-2 right-2 rounded-md bg-black/55 px-1.5 py-1 text-[9.5px] leading-tight text-white">
                {headline}
              </div>
            </div>
          </div>
        )}

        {/* Pinterest pin */}
        {showPinterest && (
          <div className="w-[105px]">
            <Label platform="pinterest">Pinterest</Label>
            <div
              className={`overflow-hidden rounded-lg border border-border bg-white ${previewHint}`}
              onClick={() => setPreviewFormat('pinterest_standard_pin')}
              title="Click to preview full size"
            >
              <div className="relative aspect-[2/3] overflow-hidden">
                <ConceptArt concept={concept} index={index} fallbackUrl={fallbackUrl} objectPosition={cropPos('pinterest_standard_pin')} format="standard_pin" />
                <div className="absolute left-1.5 top-1.5 rounded-full bg-white/90 px-1.5 py-0.5 text-[8px] font-semibold text-gray-700">
                  Promoted
                </div>
                <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#E60023] shadow">
                  <svg className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </div>
              </div>
              <div className="px-1.5 pb-1.5 pt-1">
                <p className="truncate text-[8.5px] font-semibold leading-tight text-gray-900">{headline}</p>
                <div className="mt-1 flex items-center gap-1">
                  <span className="h-3.5 w-3.5 flex-shrink-0 rounded-full bg-gray-300" />
                  <span className="min-w-0 flex-1 truncate text-[7.5px] text-gray-600">Your Brand</span>
                  <span className="flex-shrink-0 rounded-full border border-gray-300 px-1 py-px text-[7px] text-gray-600">Follow</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* X promoted post */}
        {showTwitter && (
          <div className="w-[185px]">
            <Label platform="twitter">X promoted</Label>
            <div
              className={`overflow-hidden rounded-lg border border-border bg-white text-[10.5px] text-gray-900 ${previewHint}`}
              onClick={() => setPreviewFormat('twitter_promoted')}
              title="Click to preview full size"
            >
              <div className="flex gap-1.5 px-2 py-1.5">
                <span className="h-[18px] w-[18px] flex-shrink-0 rounded-full bg-gray-300" />
                <span className="min-w-0 leading-tight">
                  <b className="block text-[10.5px]">Your Brand</b>
                  <span className="block text-[9px] text-gray-500">Promoted</span>
                </span>
              </div>
              <p className="px-2 pb-1.5 text-[9.5px] leading-snug line-clamp-3">{tweetText}</p>
              {/* Website card: 1.91:1 image + headline strip */}
              <div className="mx-2 mb-2 overflow-hidden rounded-lg border border-gray-200">
                <div className="relative aspect-[1.91/1] overflow-hidden">
                  <ConceptArt concept={concept} index={index} fallbackUrl={fallbackUrl} />
                </div>
                <div className="bg-[#f7f9f9] px-1.5 py-1">
                  <p className="truncate text-[8.5px] font-medium text-gray-900">{headline}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Google search + display */}
        {(showSearch || showDisplay) && (
          <div className="min-w-[210px] flex-1">
            {showSearch && (
              <>
                <Label platform="google_ads">
                  Google search <span className="normal-case tracking-normal">(text assets — no image)</span>
                </Label>
                <div
                  className={`rounded-lg border border-border bg-white px-3 py-2 ${previewHint}`}
                  onClick={() => setPreviewFormat('google_search')}
                  title="Click to preview full size"
                >
                  <p className="text-[9.5px] font-semibold text-gray-600">Sponsored · yourbrand.com</p>
                  <p className="my-0.5 text-[12.5px] text-[#1a0dab]">{gHeadline}</p>
                  <p className="text-[10.5px] leading-snug text-[#4d5156]">{gDescription}</p>
                </div>
              </>
            )}
            {showDisplay && (
              <>
                <Label platform="google_ads">
                  <span className={showSearch ? 'mt-2.5 inline-block' : ''}>Google display</span>
                </Label>
                <div
                  className={`flex items-center gap-2.5 rounded-lg border border-border bg-white px-2.5 py-2 ${previewHint}`}
                  onClick={() => setPreviewFormat('google_display')}
                  title="Click to preview full size"
                >
                  <span className="relative h-[33px] w-[63px] flex-shrink-0 overflow-hidden rounded">
                    <ConceptArt concept={concept} index={index} fallbackUrl={fallbackUrl} objectPosition={cropPos('google_display')} format="display" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-[11px] text-gray-900">{headline}</b>
                    <span className="mt-1 inline-block rounded border border-[#1a73e8] px-2 py-0.5 text-[9.5px] text-[#1a73e8]">
                      Learn more
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {previewFormat && (
        <AdPreviewModal
          state={state}
          concept={concept}
          initialFormat={previewFormat}
          conceptIndex={index}
          uploadedImages={uploadedImages}
          onAdjustmentSave={
            onAdjustmentSave
              ? (fmt, crop, w, h) => onAdjustmentSave(concept.id, fmt, crop, w, h)
              : undefined
          }
          onClose={() => setPreviewFormat(null)}
        />
      )}
    </>
  );
};
